import React, { useState, useEffect } from 'react';
import { ContactWithDetails, Contact, Phone, Email } from '../../../crm/components/contacts/types';
import { FormErrors } from '../../../types';
import { useResidentData } from '../../hooks/useResidentData';
import { useResidentContacts } from './hooks/useResidentContacts';
import { useUnsavedChanges } from '../../../hooks/useUnsavedChanges';
import { validateRequired, validatePhoneNumber, validateEmail } from '../../../utils/validation';
import { residentApi } from '../../services/residentApi';
import ResidentPhoneSection from './ResidentPhoneSection';
import ResidentEmailSection from './ResidentEmailSection';
import ContactInfoSection from './ContactInfoSection';
import { LoadingBar } from '../../../components/common/LoadingBar';
import { ActionButton } from '../../../components/common/ActionButton';
import { useResidentCategories } from './hooks/useResidentCategories';
import { formatCategoryName } from '../../../components/common';
import '../../../components/common/CRMForm.css';

interface ResidentContactFormProps {
  contact: ContactWithDetails | null;
  onSave: () => void;
  onCancel: () => void;
}

const ResidentContactForm: React.FC<ResidentContactFormProps> = ({ contact, onSave, onCancel }) => {
  const isEditing = !!contact;
  const { createContact, updateContact, contacts } = useResidentContacts();
  const { categories } = useResidentCategories();
  const {
    contact: userContact,
    phones: userPhones,
    emails: userEmails,
    fetchContact,
    fetchPhones,
    fetchEmails,
    createPhone,
    updatePhone,
    deletePhone,
    createEmail,
    updateEmail,
    deleteEmail,
  } = useResidentData();

  // Check if this is a Tenant contact
  const isTenantContact = contact ? formatCategoryName(contact.typeId, categories) === 'Tenant' : false;

  // Form state
  const [formData, setFormData] = useState({
    typeId: 0,
    firstName: '',
    lastName: '',
    businessName: '',
    notes: ''
  });

  const [phones, setPhones] = useState<Phone[]>([]);
  const [emails, setEmails] = useState<Email[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    phones: !isEditing,
    emails: !isEditing,
  });

  // Check if this is the user's own contact
  const isOwnContact = !!(contact && userContact && contact.id === userContact.id);

  // Initialize form data
  useEffect(() => {
    if (contact) {
      setFormData({
        typeId: contact.typeId,
        firstName: contact.firstName,
        lastName: contact.lastName,
        businessName: contact.businessName,
        notes: contact.notes
      });
      setPhones(contact.phones || []);
      setEmails(contact.emails || []);
      setExpandedSections({
        phones: false,
        emails: false,
      });
      
      // Only fetch phones/emails if this is the user's own contact
      if (isOwnContact) {
        fetchPhones();
        fetchEmails();
      }
    } else {
      setFormData({
        typeId: 0,
        firstName: '',
        lastName: '',
        businessName: '',
        notes: ''
      });
      setPhones([]);
      setEmails([]);
      setExpandedSections({
        phones: true,
        emails: true,
      });
    }
    setHasChanges(false);
    setErrors({});
  }, [contact, isOwnContact, fetchPhones, fetchEmails]);

  // Sync phones and emails from useResidentData when it's user's own contact
  useEffect(() => {
    if (isOwnContact && userPhones.length > 0) {
      setPhones(userPhones);
    }
  }, [isOwnContact, userPhones]);

  useEffect(() => {
    if (isOwnContact && userEmails.length > 0) {
      setEmails(userEmails);
    }
  }, [isOwnContact, userEmails]);

  // Fetch user contact on mount to check ownership
  useEffect(() => {
    fetchContact();
  }, [fetchContact]);

  // Track changes
  useEffect(() => {
    if (isEditing && contact) {
      const hasContactChanges = 
        formData.typeId !== contact.typeId ||
        formData.firstName !== contact.firstName ||
        formData.lastName !== contact.lastName ||
        formData.businessName !== contact.businessName ||
        formData.notes !== contact.notes;
      
      const hasPhoneChanges = JSON.stringify(phones) !== JSON.stringify(contact.phones || []);
      const hasEmailChanges = JSON.stringify(emails) !== JSON.stringify(contact.emails || []);
      
      setHasChanges(hasContactChanges || (isOwnContact && (hasPhoneChanges || hasEmailChanges)));
    } else if (!isEditing) {
      const hasAnyData = 
        formData.firstName.trim() !== '' ||
        formData.lastName.trim() !== '' ||
        formData.businessName.trim() !== '' ||
        formData.notes.trim() !== '' ||
        phones.length > 0 ||
        emails.length > 0;
      
      setHasChanges(hasAnyData);
    }
  }, [formData, phones, emails, isEditing, contact, isOwnContact]);

  const { confirmNavigation } = useUnsavedChanges({ hasChanges });

  const handleInputChange = (field: keyof Contact, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (formData.typeId === 0) {
      newErrors.type = 'Contact type is required';
    }

    // Validate Emergency contact duplicates
    if (formData.typeId > 0) {
      const selectedCategory = categories.find(cat => cat.id === formData.typeId);
      if (selectedCategory) {
        const emergencyNames = ['Emergency 1', 'Emergency 2', 'Emergency 3'];
        if (emergencyNames.includes(selectedCategory.name)) {
          // Check if another contact already has this Emergency type
          const existingEmergency = contacts.find(c => 
            c.id !== contact?.id && c.typeId === formData.typeId
          );
          if (existingEmergency) {
            newErrors.type = `An Emergency contact with type "${selectedCategory.name}" already exists. Only one of each Emergency type is allowed.`;
          }
        }
      }
    }

    const firstNameError = validateRequired(formData.firstName, 'First name');
    if (firstNameError) newErrors.firstName = firstNameError;

    const lastNameError = validateRequired(formData.lastName, 'Last name');
    if (lastNameError) newErrors.lastName = lastNameError;

    // Only validate phones/emails if this is the user's own contact
    if (isOwnContact || !isEditing) {
      const phoneErrors: string[] = [];
      phones.forEach((phone, index) => {
        if (phone.typeId === 0) {
          phoneErrors.push(`Phone ${index + 1}: Please select a phone type`);
        }
        if (!phone.phoneNumber.trim()) {
          phoneErrors.push(`Phone ${index + 1}: Phone number is required`);
        } else if (!validatePhoneNumber(phone.phoneNumber)) {
          phoneErrors.push(`Phone ${index + 1}: Please enter a valid phone number`);
        }
      });
      
      if (phoneErrors.length > 0) {
        newErrors.phones = phoneErrors.join(', ');
      }

      const emailErrors: string[] = [];
      emails.forEach((email, index) => {
        if (email.typeId === 0) {
          emailErrors.push(`Email ${index + 1}: Please select an email type`);
        }
        if (!email.emailAddress.trim()) {
          emailErrors.push(`Email ${index + 1}: Email address is required`);
        } else if (!validateEmail(email.emailAddress)) {
          emailErrors.push(`Email ${index + 1}: Please enter a valid email address`);
        }
      });
      
      if (emailErrors.length > 0) {
        newErrors.emails = emailErrors.join(', ');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      let savedContact: Contact;
      
      if (isEditing && contact) {
        savedContact = await updateContact(contact.id, formData);
      } else {
        savedContact = await createContact(formData);
      }

      // Save phones and emails
      // For user's own contact, use useResidentData methods
      // For new contacts or other contacts, use the new contact-specific API methods
      if (isOwnContact) {
        await Promise.all([
          savePhones(savedContact.id),
          saveEmails(savedContact.id)
        ]);
      } else {
        // For new contacts or contacts that aren't the user's own, use contact-specific endpoints
        await Promise.all([
          savePhonesForContact(savedContact.id),
          saveEmailsForContact(savedContact.id)
        ]);
      }

      setHasChanges(false);
      onSave();
    } catch (error: any) {
      console.error('Error saving contact:', error);
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to save contact. Please try again.';
      setErrors({ general: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const savePhones = async (contactId: number) => {
    const validPhones = phones.filter(phone => 
      phone.typeId > 0 && 
      phone.phoneNumber.trim() !== '' && 
      validatePhoneNumber(phone.phoneNumber)
    );

    if (isEditing && contact) {
      const originalPhones = contact.phones || [];
      
      const phonesToDelete = originalPhones.filter(originalPhone => 
        !validPhones.some(currentPhone => currentPhone.id === originalPhone.id)
      );
      
      const phonesToUpdate = validPhones.filter(currentPhone => {
        if (currentPhone.id === 0) return false;
        const originalPhone = originalPhones.find(p => p.id === currentPhone.id);
        if (!originalPhone) return false;
        
        return (
          currentPhone.typeId !== originalPhone.typeId ||
          currentPhone.phoneNumber !== originalPhone.phoneNumber ||
          currentPhone.isPrimary !== originalPhone.isPrimary
        );
      });
      
      const phonesToCreate = validPhones.filter(phone => phone.id === 0);
      
      await Promise.all([
        ...phonesToDelete.map(phone => deletePhone(phone.id)),
        ...phonesToUpdate.map(phone => updatePhone(phone.id, {
          typeId: phone.typeId,
          phoneNumber: phone.phoneNumber,
          isPrimary: phone.isPrimary,
        })),
        ...phonesToCreate.map(phone => createPhone({
          typeId: phone.typeId,
          phoneNumber: phone.phoneNumber,
          isPrimary: phone.isPrimary,
        }))
      ]);
    } else {
      await Promise.all(
        validPhones.map(phone => createPhone({
          typeId: phone.typeId,
          phoneNumber: phone.phoneNumber,
          isPrimary: phone.isPrimary,
        }))
      );
    }
  };

  const saveEmails = async (contactId: number) => {
    const validEmails = emails.filter(email => 
      email.typeId > 0 && 
      email.emailAddress.trim() !== '' && 
      validateEmail(email.emailAddress)
    );

    if (isEditing && contact) {
      const originalEmails = contact.emails || [];
      
      const emailsToDelete = originalEmails.filter(originalEmail => 
        !validEmails.some(currentEmail => currentEmail.id === originalEmail.id)
      );
      
      const emailsToUpdate = validEmails.filter(currentEmail => {
        if (currentEmail.id === 0) return false;
        const originalEmail = originalEmails.find(e => e.id === currentEmail.id);
        if (!originalEmail) return false;
        
        return (
          currentEmail.typeId !== originalEmail.typeId ||
          currentEmail.emailAddress !== originalEmail.emailAddress ||
          currentEmail.isPrimary !== originalEmail.isPrimary
        );
      });
      
      const emailsToCreate = validEmails.filter(email => email.id === 0);
      
      await Promise.all([
        ...emailsToDelete.map(email => deleteEmail(email.id)),
        ...emailsToUpdate.map(email => updateEmail(email.id, {
          typeId: email.typeId,
          emailAddress: email.emailAddress,
          isPrimary: email.isPrimary,
        })),
        ...emailsToCreate.map(email => createEmail({
          typeId: email.typeId,
          emailAddress: email.emailAddress,
          isPrimary: email.isPrimary,
        }))
      ]);
    } else {
      await Promise.all(
        validEmails.map(email => createEmail({
          typeId: email.typeId,
          emailAddress: email.emailAddress,
          isPrimary: email.isPrimary,
        }))
      );
    }
  };

  const savePhonesForContact = async (contactId: number) => {
    const validPhones = phones.filter(phone => 
      phone.typeId > 0 && 
      phone.phoneNumber.trim() !== '' && 
      validatePhoneNumber(phone.phoneNumber)
    );

    if (isEditing && contact) {
      const originalPhones = contact.phones || [];
      
      // Find phones to delete (exist in original but not in current)
      const phonesToDelete = originalPhones.filter(originalPhone => 
        !validPhones.some(currentPhone => currentPhone.id === originalPhone.id)
      );
      
      // Find phones to update (exist in both but have changes)
      const phonesToUpdate = validPhones.filter(currentPhone => {
        if (currentPhone.id === 0) return false; // Skip new phones (id === 0)
        const originalPhone = originalPhones.find(p => p.id === currentPhone.id);
        if (!originalPhone) return false;
        
        return (
          currentPhone.typeId !== originalPhone.typeId ||
          currentPhone.phoneNumber !== originalPhone.phoneNumber ||
          currentPhone.isPrimary !== originalPhone.isPrimary
        );
      });
      
      // Find phones to create (new phones with id === 0)
      const phonesToCreate = validPhones.filter(phone => phone.id === 0);
      
      // Use residentApi methods for updates and deletes
      await Promise.all([
        ...phonesToDelete.map(phone => residentApi.deletePhone(phone.id)),
        ...phonesToUpdate.map(phone => residentApi.updatePhone(phone.id, {
          typeId: phone.typeId,
          phoneNumber: phone.phoneNumber,
          isPrimary: phone.isPrimary,
        })),
        ...phonesToCreate.map(phone => residentApi.createPhoneForContact(contactId, {
          typeId: phone.typeId,
          phoneNumber: phone.phoneNumber,
          isPrimary: phone.isPrimary,
        }))
      ]);
    } else {
      // For new contacts, create all valid phones
      await Promise.all(
        validPhones.map(phone => residentApi.createPhoneForContact(contactId, {
          typeId: phone.typeId,
          phoneNumber: phone.phoneNumber,
          isPrimary: phone.isPrimary,
        }))
      );
    }
  };

  const saveEmailsForContact = async (contactId: number) => {
    const validEmails = emails.filter(email => 
      email.typeId > 0 && 
      email.emailAddress.trim() !== '' && 
      validateEmail(email.emailAddress)
    );

    if (isEditing && contact) {
      const originalEmails = contact.emails || [];
      
      // Find emails to delete (exist in original but not in current)
      const emailsToDelete = originalEmails.filter(originalEmail => 
        !validEmails.some(currentEmail => currentEmail.id === originalEmail.id)
      );
      
      // Find emails to update (exist in both but have changes)
      const emailsToUpdate = validEmails.filter(currentEmail => {
        if (currentEmail.id === 0) return false; // Skip new emails (id === 0)
        const originalEmail = originalEmails.find(e => e.id === currentEmail.id);
        if (!originalEmail) return false;
        
        return (
          currentEmail.typeId !== originalEmail.typeId ||
          currentEmail.emailAddress !== originalEmail.emailAddress ||
          currentEmail.isPrimary !== originalEmail.isPrimary
        );
      });
      
      // Find emails to create (new emails with id === 0)
      const emailsToCreate = validEmails.filter(email => email.id === 0);
      
      // Use residentApi methods for updates and deletes
      await Promise.all([
        ...emailsToDelete.map(email => residentApi.deleteEmail(email.id)),
        ...emailsToUpdate.map(email => residentApi.updateEmail(email.id, {
          typeId: email.typeId,
          emailAddress: email.emailAddress,
          isPrimary: email.isPrimary,
        })),
        ...emailsToCreate.map(email => residentApi.createEmailForContact(contactId, {
          typeId: email.typeId,
          emailAddress: email.emailAddress,
          isPrimary: email.isPrimary,
        }))
      ]);
    } else {
      // For new contacts, create all valid emails
      await Promise.all(
        validEmails.map(email => residentApi.createEmailForContact(contactId, {
          typeId: email.typeId,
          emailAddress: email.emailAddress,
          isPrimary: email.isPrimary,
        }))
      );
    }
  };

  const handleCancel = () => {
    if (hasChanges && !confirmNavigation()) {
      return;
    }
    onCancel();
  };

  return (
    <div className="form">
      <div className="form-header">
        <h2>{isEditing ? 'Edit Contact' : 'Create Contact'}</h2>
        {hasChanges && <span className="form-unsaved-indicator">• Unsaved changes</span>}
      </div>

      {errors.general && (
        <div className="app-error-message">{errors.general}</div>
      )}

      <div className="form-content">
        <section className="form-section">
          <h3>Contact Information</h3>
          {isTenantContact && (
            <div className="app-info-message" style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#e0f2fe', border: '1px solid #0284c7', borderRadius: '4px', color: '#0c4a6e' }}>
              Tenant contacts can be updated but cannot be deleted or have their type changed.
            </div>
          )}
          <ContactInfoSection
            contactData={formData}
            errors={errors}
            onChange={handleInputChange}
            isTypeDisabled={isTenantContact}
            isReadOnly={false}
            existingContacts={contacts}
            currentContactId={contact?.id || null}
          />
        </section>

        {/* Always show phones/emails sections, but make read-only if not user's own contact */}
        <section className="form-section">
          <div 
            className="form-section-header"
            onClick={() => toggleSection('phones')}
          >
            <h3>Phone Numbers ({phones.length})</h3>
            <span className={`form-section-toggle ${expandedSections.phones ? 'expanded' : ''}`}>
              ▼
            </span>
          </div>
          
          {errors.phones && (
            <div className="app-form-error">{errors.phones}</div>
          )}
          
          {expandedSections.phones && (
            <ResidentPhoneSection
              phones={phones}
              onChange={setPhones}
            />
          )}
        </section>

        <section className="form-section">
          <div 
            className="form-section-header"
            onClick={() => toggleSection('emails')}
          >
            <h3>Email Addresses ({emails.length})</h3>
            <span className={`form-section-toggle ${expandedSections.emails ? 'expanded' : ''}`}>
              ▼
            </span>
          </div>
          
          {errors.emails && (
            <div className="app-form-error">{errors.emails}</div>
          )}
          
          {expandedSections.emails && (
            <ResidentEmailSection
              emails={emails}
              onChange={setEmails}
            />
          )}
        </section>
      </div>

      <div className="app-form-actions">
        <button
          type="button"
          className="app-btn app-btn-secondary"
          onClick={handleCancel}
          disabled={loading}
        >
          Cancel
        </button>
        <ActionButton
          className="app-btn app-btn-primary"
          label={isEditing ? 'Update' : 'Create'}
          busyLabel="Saving..."
          busy={loading}
          disabled={!hasChanges}
          onClick={() => handleSave()}
        />
      </div>
    </div>
  );
};

export default ResidentContactForm;
